import { Box, SxProps, Tab, Tabs, Theme } from '@mui/material';
import { ReactNode, useState } from 'react';

interface TabPanelProps {
 children: ReactNode;
 index: number;
 value: number;
 sx?: SxProps<Theme>;
}
interface TabItemProps {
 id: string;
 label: string;
 content: ReactNode;
 disabled?: boolean;
}
interface ColModalTabsResponsiveProps {
 tabs: TabItemProps[];
 defaultTab?: number;
 variant?: 'fullWidth' | 'standard' | 'scrollable';
 ariaLabel?: string;
 sx?: SxProps<Theme>;
 tabSx?: SxProps<Theme>;
 panelSx?: SxProps<Theme>;
 onChange?: (newValue: number) => void;
}

function a11yProps(index: number) {
 return {
  id: `ColModalTabsResponsive-tab-${index}`,
  'aria-controls': `ColModalTabsResponsive-tabpanel-${index}`,
 };
}

function CustomTabPanel(props: TabPanelProps) {
 const { children, value, index, ...other } = props;

 return (
  <Box
   role="tabpanel"
   hidden={value !== index}
   id={`ColModalTabsResponsive-tabpanel-${index}`}
   aria-labelledby={`ColModalTabsResponsive-tab-${index}`}
   {...other}
  >
   {value === index && <Box sx={{ py: 2, px: 3 }}>{children}</Box>}
  </Box>
 );
}

const ColModalTabsResponsive = ({
 tabs,
 defaultTab = 0,
 variant = 'fullWidth',
 ariaLabel = 'Tabs',
 sx,
 tabSx,
 panelSx,
 onChange,
}: ColModalTabsResponsiveProps) => {
 const [tab, setTab] = useState(defaultTab);

 const handleChange = (event: React.SyntheticEvent, newValue: number) => {
  setTab(newValue);
  onChange?.(newValue);
 };

 const defaultTabSx = {
  '& .Mui-selected': {
   backgroundColor: (theme: Theme) => theme.palette.kemek.white,
  },
  '& .MuiButtonBase-root.MuiTab-root': {
   pt: '12px',
   pb: '12px',
   fontSize: '14px',
  },
 };

 const defaultPanelSx = {
  p: 0,
 };

 return (
  <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
   <Box
    sx={{
     borderBottom: 1,
     borderBottomColor: (theme: Theme) => theme.palette.kemek.lightestGray,
     mt: '-1px',
     flexShrink: 0, // Keep tabs fixed
     ...sx,
    }}
   >
    <Tabs
     variant={variant}
     value={tab}
     onChange={handleChange}
     aria-label={ariaLabel}
     sx={{
      ...defaultTabSx,
      ...tabSx,
     }}
    >
     {tabs.map((tabItem, index) => (
      <Tab
       key={tabItem.id}
       label={tabItem.label}
       disabled={tabItem.disabled}
       {...a11yProps(index)}
      />
     ))}
    </Tabs>
   </Box>

   <Box sx={{ flex: 1, overflowY: 'auto' }}>
    {tabs.map((tabItem, index) => (
     <CustomTabPanel
      key={tabItem.id}
      value={tab}
      index={index}
      sx={{ height: '100%' }}
     >
      <Box sx={{ ...defaultPanelSx, ...panelSx, height: '100%' }}>
       {tabItem.content}
      </Box>
     </CustomTabPanel>
    ))}
   </Box>
  </Box>
 );
};

export default ColModalTabsResponsive;
